import { Component } from '@theme/component';

class TabsComponent extends Component {
  requiredRefs = ['tabList', 'panels'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);

    this.#wrapDirectGroupBlocksAsTabpanels();
    this.#buildTabsFromPanels();

    // Ensure panels reflect initial selected tab on load
    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    const initialIndex = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    this.#activate(initialIndex === -1 ? 0 : initialIndex);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Direct children under Tabs are often Group blocks (no role="tabpanel").
   * Wrap them so aria-controls / keyboard behavior matches the tab buttons.
   */
  #wrapDirectGroupBlocksAsTabpanels() {
    const panelsRoot = this.refs.panels;
    if (!(panelsRoot instanceof HTMLElement)) return;
    if (panelsRoot.dataset.autoTabPanels !== 'true') return;

    const directChildren = Array.from(panelsRoot.children);
    for (const child of directChildren) {
      if (!(child instanceof HTMLElement)) continue;
      if (child.getAttribute('role') === 'tabpanel') continue;
      const bid = child.dataset.blockId;
      if (!bid) continue;

      const wrap = document.createElement('div');
      wrap.setAttribute('role', 'tabpanel');
      wrap.setAttribute('aria-labelledby', `tab-${bid}`);
      wrap.className = 'tabs-component__panel';
      wrap.id = `panel-${bid}`;
      child.replaceWith(wrap);
      wrap.appendChild(child);
    }
  }

  #buildTabsFromPanels() {
    const tabList = this.refs.tabList;
    const panelsRoot = this.refs.panels;
    if (!(tabList instanceof HTMLElement) || !(panelsRoot instanceof HTMLElement)) return;

    const panels = Array.from(panelsRoot.querySelectorAll('[role="tabpanel"]'));
    if (!panels.length) return;

    // Clear any server-rendered tabs
    tabList.replaceChildren();

    panels.forEach((panel, index) => {
      if (!(panel instanceof HTMLElement)) return;
      const panelId = panel.id;
      if (!panelId || !panelId.startsWith('panel-')) return;

      const bid = panelId.replace(/^panel-/, '');
      const label =
        panel.dataset.tabLabel ||
        panel.querySelector('[data-tab-label]')?.getAttribute('data-tab-label') ||
        'Tab';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tabs-component__tab';
      button.setAttribute('role', 'tab');
      button.id = `tab-${bid}`;
      button.setAttribute('aria-controls', panelId);
      button.dataset.tabIndex = String(index);
      button.textContent = label;

      const selected = index === 0;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.setAttribute('tabindex', selected ? '0' : '-1');

      tabList.appendChild(button);
    });
  }

  /** @param {number} index */
  #activate(index) {
    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    const panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!tabs.length || !panels.length) return;

    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });

    panels.forEach((panel, i) => {
      if (i === index) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  /** @param {MouseEvent} e */
  #onClick = (e) => {
    const target = /** @type {HTMLElement | null} */ (e.target instanceof HTMLElement ? e.target : null);
    const tab = target?.closest?.('[role="tab"]');
    if (!(tab instanceof HTMLElement)) return;
    const index = Number.parseInt(tab.dataset.tabIndex || '', 10);
    if (Number.isNaN(index)) return;
    this.#activate(index);
  };

  /** @param {KeyboardEvent} e */
  #onKeyDown = (e) => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || active.getAttribute('role') !== 'tab') return;

    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    const currentIndex = tabs.findIndex((t) => t === active);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (e.key === 'Home') nextIndex = 0;
    if (e.key === 'End') nextIndex = tabs.length - 1;

    if (nextIndex !== currentIndex) {
      e.preventDefault();
      const nextTab = tabs[nextIndex];
      if (nextTab instanceof HTMLElement) nextTab.focus();
      this.#activate(nextIndex);
    }
  };
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}

